import { CrossIcon } from "lucide-react";
import { RxCross1 } from "react-icons/rx";
/**
 * @typedef {Object} UserData
 * @property {string | number | boolean | null | undefined} [key] - Any key in the user object can be a string, number, boolean, or null/undefined.
 * @property {Array<any> | Object<string, any>} [nestedKey] - Keys can also hold arrays or nested objects.
 */
/**
* Renders a value based on its type (string, number, boolean, array, or object).
*
* @param {any} value - The value to be rendered.
* @param {string} [key=''] - The key of the current value in its parent object (mostly for debugging/context).
* @returns {JSX.Element} The JSX element representing the rendered value.
*/
const renderValue = (value, key = '') => {
    if (value === null || value === undefined || value == "") {
        return <span className="text-[var(--secondary-text-color)]">N/A</span>;
    }
    if (typeof value === 'boolean') {
        return <span className={`text-${value ? 'green' : 'red'}-500 font-medium`}>{value ? 'Yes' : 'No'}</span>;
    }
    if (typeof value === 'string' || typeof value === 'number') {
        return <span className="text-[var(--primary-text-color)] font-medium break-all">{value}</span>;
    }
    if (Array.isArray(value)) {
        if (value.length === 0) return <span className="text-[var(--secondary-text-color)]">None</span>;
        return (
            <div className="space-y-2 ">
                {value.map((item, idx) => (
                    <div key={idx} className="p-3 bg-[var(--color-surface-card)] rounded-lg border border-[var(--border-color)]">
                        {typeof item === 'string' ? (
                            <span className="text-[var(--primary-text-color)]">{item}</span>
                        ) : (
                            Object.entries(item).map(([subKey, subValue]) => (
                                <div key={subKey} className="text-sm mb-1 last:mb-0 flex justify-between">
                                    <span className="text-[var(--secondary-text-color)] capitalize">{subKey}:</span>
                                    <span className="text-[var(--primary-text-color)] ml-2 font-medium">{subValue}</span>
                                </div>
                            ))
                        )}
                    </div>
                ))}
            </div>
        );
    }
    if (typeof value === 'object') {
        return (
            <div className="space-y-4 ml-4">
                {Object.entries(value).map(([subKey, subValue]) => (
                    <div key={subKey} className="border border-[var(--border-color)] rounded-lg p-3">
                        <div className="text-[var(--secondary-text-color)] uppercase text-xs font-medium mb-2 tracking-wider">
                            {subKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </div>
                        <div>{renderValue(subValue, subKey)}</div>
                    </div>
                ))}
            </div>
        );
    }
    return <span className="text-[var(--secondary-text-color)]">Unknown</span>;
};


/**
 * @typedef {Object} EventDataPopupProps
 * @property {UserData} user - The data object containing user/event details to be displayed in the popup.
 * @property {() => void} onClose - Function to be called when the popup is closed.
 */

/**
 * A modal/popup component to display structured user or event data.
 * It intelligently categorizes and renders different data types (primitives, arrays, objects).
 *
 * @param {EventDataPopupProps} props - The component props.
 * @returns {JSX.Element} The rendered React component.
 */
export const EventDataPopup = ({ user, onClose }) => {


    const formatKey = (key) => {
        return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
    };

    // Categorize and sort entries
    const entries = Object.entries(user);
    const primitives = entries
        .filter(([_, v]) => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')
        .sort(([a], [b]) => a.localeCompare(b));
    const arrays = entries
        .filter(([_, v]) => Array.isArray(v))
        .sort(([a], [b]) => a.localeCompare(b));
    const objects = entries
        .filter(([_, v]) => v !== null && typeof v === 'object' && !Array.isArray(v))
        .sort(([a], [b]) => a.localeCompare(b));

    const sortedEntries = [...primitives, ...arrays, ...objects];



    /**
     * Renders a single top-level key-value entry in its display container.
     *
     * @param {[string, any]} entry - A tuple containing the key and value of the data entry.
     * @returns {JSX.Element} The JSX element for a single data entry block.
     */
    const renderEntry = ([key, value]) => (
        <div key={key} className="space-y-2">
            <div className="text-[var(--secondary-text-color)] uppercase text-xs font-medium tracking-wider mb-2">
                {formatKey(key)}
            </div>
            <div className="bg-[var(--color-surface-card)] p-4 rounded-lg border border-[var(--border-color)]">
                {renderValue(value, key)}
            </div>
        </div>
    );

    return (
        <div className="  flex items-center justify-center z-50 p-4 absolute inset-0 backdrop-blur-xs w-full ">
            <div
                className="bg-[var(--color-surface-background)] rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-[var(--border-color-black)] relative shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold text-[var(--primary-text-color)]">User Details</h2>
                    <button
                        onClick={onClose}
                        className="text-[var(--secondary-text-color)] hover:text-[var(--primary-text-color)] transition-colors p-1 rounded cursor-pointer"
                    >
                        <RxCross1 />
                    </button>
                </div>
                <div className="space-y-6">
                    {/* Primitives in a 2-column grid */}
                    {primitives.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {primitives.map(renderEntry)}
                        </div>
                    )}
                    {/* Arrays and Objects in a 2-column grid */}
                    {(arrays.length > 0 || objects.length > 0) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {arrays.map(renderEntry)}
                            {objects.map(renderEntry)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};